/* ==========================================================================
   His Lightkeepers — shared motion utilities (vanilla, no dependencies)

   Three jobs, all optional enhancements. If this file fails to load or JS is
   disabled, every section still renders in its final, readable state.

     1. LK.reveal   — IntersectionObserver scroll reveals
     2. LK.parallax — rAF-throttled, transform-only depth movement
     3. LK.flicker  — randomised lightning timing (no loop-feel, no video)

   Rules this file obeys:
     - never animates layout properties, only transform/opacity
     - never computes layout inside a scroll event; all reads happen once per
       frame inside requestAnimationFrame
     - honours prefers-reduced-motion at runtime, including live changes
     - honours a per-element mobile budget via data-lk-mobile="off"
   ========================================================================== */

(function () {
  'use strict';

  /* Idempotency guard. This script is loaded once from layout/theme.liquid,
     but a theme-editor section reload or a future stray script tag must never
     be able to register a second set of scroll and resize listeners. Earlier
     this file executed 23 times on the homepage. */
  if (window.LK && window.LK.__initialised) return;

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 749px)');

  var LK = {
    get reducedMotion() {
      return motionQuery.matches;
    },
    get isMobile() {
      return mobileQuery.matches;
    }
  };

  /* --- 1. Scroll reveals -------------------------------------------------
     Elements opt in with data-lk-reveal. We add the "armed" class from JS so
     that the hidden state only ever exists when we can guarantee we will
     un-hide it. data-lk-reveal-delay staggers a group in ms. */
  var revealObserver = null;

  function armReveal(el) {
    if (el.dataset.lkRevealArmed === 'true') return;
    el.dataset.lkRevealArmed = 'true';

    if (LK.reducedMotion) {
      el.classList.add('lk-reveal--in');
      return;
    }

    var delay = parseInt(el.dataset.lkRevealDelay || '0', 10);
    if (delay) el.style.setProperty('--lk-reveal-delay', delay + 'ms');
    el.classList.add('lk-reveal--armed');
    revealObserver.observe(el);
  }

  function initReveals(root) {
    var targets = (root || document).querySelectorAll('[data-lk-reveal]');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('lk-reveal--in');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('lk-reveal--in');
            revealObserver.unobserve(entry.target); // reveal once, then forget
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
      );
    }

    targets.forEach(armReveal);
  }

  /* --- 2. Parallax -------------------------------------------------------
     Each participating element carries:
       data-lk-parallax        — the element is a depth layer
       data-lk-depth="0.18"    — how far it travels, as a share of scroll
       data-lk-mobile="off"    — dropped entirely on phones (motion budget)
       data-lk-scale="1.06"    — optional constant scale, keeps edges covered

     Scroll position is read once per frame. Element geometry is cached and
     only re-measured on resize, so scrolling never triggers layout. */
  var layers = [];
  var stickyHeroes = [];
  var ticking = false;
  var viewportH = window.innerHeight;

  function measure() {
    viewportH = window.innerHeight;
    layers.forEach(function (layer) {
      var rect = layer.container.getBoundingClientRect();
      layer.top = rect.top + window.scrollY;
      layer.height = rect.height;
    });

    // A pinned element's own rect stops moving, so progress is measured from
    // the page instead: how far we have scrolled past where it starts.
    stickyHeroes.forEach(function (hero) {
      var host = hero.host.getBoundingClientRect();
      hero.top = host.top + window.scrollY;
      hero.height = host.height || viewportH;
    });
  }

  function apply() {
    ticking = false;
    var scrollY = window.scrollY;

    for (var i = 0; i < layers.length; i++) {
      var layer = layers[i];

      // Progress of this container through the viewport: 0 as it enters from
      // below, 1 as it leaves past the top.
      var raw = (scrollY + viewportH - layer.top) / (layer.height + viewportH);
      var progress = raw < 0 ? 0 : raw > 1 ? 1 : raw;

      // Centre the travel so the layer sits at its authored position when the
      // container is centred in the viewport — no jump on load.
      var offset = (progress - 0.5) * layer.depth * layer.height;

      layer.el.style.transform =
        'translate3d(0,' + offset.toFixed(2) + 'px,0)' +
        (layer.scale ? ' scale(' + layer.scale + ')' : '');

      if (layer.progressVar) {
        layer.container.style.setProperty('--lk-progress', progress.toFixed(4));
      }
    }

    for (var s = 0; s < stickyHeroes.length; s++) {
      var hero = stickyHeroes[s];
      var travelled = (scrollY - hero.top) / hero.height;
      var p = travelled < 0 ? 0 : travelled > 1 ? 1 : travelled;
      hero.el.style.setProperty('--lk-progress', p.toFixed(4));
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(apply);
  }

  function resetLayers() {
    layers.forEach(function (layer) {
      layer.el.style.transform = layer.scale ? 'scale(' + layer.scale + ')' : '';
    });
    layers = [];
  }

  function initParallax(root) {
    var scope = root || document;
    var found = scope.querySelectorAll('[data-lk-parallax]');
    if (!found.length) return;

    // Rebuild the registry for this scope, then re-collect everything so a
    // section re-render in the theme editor cannot leave stale nodes behind.
    layers = layers.filter(function (layer) {
      return document.body.contains(layer.el);
    });

    if (LK.reducedMotion) {
      resetLayers();
      return;
    }

    found.forEach(function (el) {
      if (LK.isMobile && el.dataset.lkMobile === 'off') {
        el.style.transform = '';
        return;
      }
      if (layers.some(function (l) { return l.el === el; })) return;

      layers.push({
        el: el,
        container: el.closest('[data-lk-parallax-root]') || el.parentElement,
        depth: parseFloat(el.dataset.lkDepth || '0.15'),
        scale: el.dataset.lkScale || '',
        progressVar: el.hasAttribute('data-lk-progress'),
        top: 0,
        height: 0
      });
    });

    measure();
    apply();
  }

  /* --- 3. Lightning ------------------------------------------------------
     A CSS keyframe flash triggered at irregular intervals. Timing jitter is
     what stops it reading as a loop; the flash itself is pure CSS opacity on
     a transparent overlay, so it costs one composited layer and no bytes. */
  var flickerTimers = [];

  function scheduleFlash(el, min, max) {
    var wait = min + Math.random() * (max - min);
    var timer = window.setTimeout(function () {
      if (!document.body.contains(el)) return;
      if (!document.hidden && !LK.reducedMotion) {
        el.classList.remove('lk-flash');
        void el.offsetWidth; // restart the keyframe
        el.classList.add('lk-flash');
        // Occasional double-strike, the way real lightning behaves.
        if (Math.random() < 0.35) {
          window.setTimeout(function () {
            el.classList.remove('lk-flash');
            void el.offsetWidth;
            el.classList.add('lk-flash');
          }, 180 + Math.random() * 260);
        }
      }
      scheduleFlash(el, min, max);
    }, wait);
    flickerTimers.push(timer);
  }

  function initFlicker(root) {
    var targets = (root || document).querySelectorAll('[data-lk-lightning]');
    targets.forEach(function (el) {
      if (el.dataset.lkLightningOn === 'true') return;
      el.dataset.lkLightningOn = 'true';
      if (LK.reducedMotion) return;

      // Phones get a calmer storm: longer gaps, fewer strikes.
      var min = LK.isMobile ? 7000 : 3600;
      var max = LK.isMobile ? 16000 : 9000;
      scheduleFlash(el, min, max);
    });
  }

  /* --- Wiring ------------------------------------------------------------ */
  /* --- 4. Sticky hero progress -------------------------------------------
     Feeds --lk-progress (0 -> 1) while a pinned hero is being scrolled past,
     so CSS can scale the artwork and lift the copy at different rates. Runs
     inside the same rAF pass as the parallax; no extra listener, and nothing
     animates but transform and opacity. */
  function initStickyHero(root) {
    var found = (root || document).querySelectorAll('[data-lk-sticky-hero]');
    if (!found.length) return;

    stickyHeroes = stickyHeroes.filter(function (hero) {
      return document.body.contains(hero.el);
    });

    found.forEach(function (el) {
      if (stickyHeroes.some(function (h) { return h.el === el; })) return;
      stickyHeroes.push({
        el: el,
        host: el.closest('.shopify-section') || el.parentElement || el,
        top: 0,
        height: 0
      });
    });

    if (LK.reducedMotion) {
      stickyHeroes.forEach(function (hero) {
        hero.el.style.setProperty('--lk-progress', '0');
      });
      return;
    }

    measure();
    apply();
  }

  function initAll(root) {
    initReveals(root);
    initParallax(root);
    initStickyHero(root);
    initFlicker(root);
  }

  var resizeTimer = null;
  window.addEventListener(
    'resize',
    function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        measure();
        apply();
      }, 150);
    },
    { passive: true }
  );

  window.addEventListener('scroll', onScroll, { passive: true });

  // React to a live change of the OS motion preference.
  var onMotionChange = function () {
    if (LK.reducedMotion) {
      resetLayers();
      flickerTimers.forEach(window.clearTimeout);
      flickerTimers = [];
      document.querySelectorAll('[data-lk-reveal]').forEach(function (el) {
        el.classList.add('lk-reveal--in');
      });
    } else {
      initAll(document);
    }
  };
  if (motionQuery.addEventListener) motionQuery.addEventListener('change', onMotionChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor: re-init the section that was just re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function () {
    layers = layers.filter(function (layer) {
      return document.body.contains(layer.el);
    });
  });

  LK.init = initAll;
  window.LK = LK;
  LK.__initialised = true;
})();
